"use client";

import { IconBlur } from "central-icons/IconBlur";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleX } from "central-icons/IconCircleX";
import { IconColors } from "central-icons/IconColors";
import { IconDownsize } from "central-icons/IconDownsize";
import { IconSquareBehindSquare2 } from "central-icons/IconSquareBehindSquare2";
import { AnimatePresence, m, type TargetAndTransition } from "motion/react";
import DemoPanel from "@/components/preview/demo-panel";
import DemoSplit from "@/components/preview/demo-split";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import {
  DEMO_CONFIRMATION_MS,
  useCopyState,
} from "@/hooks/use-copy-to-clipboard";
import { SPRING_FAST } from "@/lib/motion";
import { cn } from "@/lib/utils";

type Swap = "all" | "none" | "opacity";

const SWAPS: Swap[] = ["none", "opacity", "all"];

const BUTTON_CLASS =
  "flex size-12 items-center justify-center rounded-xl bg-gray-100 shadow-custom transition-colors duration-200 ease-out will-change-transform hover-hover:hover:bg-gray-200";

const LEGENDS: Record<Swap, { icon: typeof IconColors; label: string }[]> = {
  all: [
    { icon: IconColors, label: "Opacity" },
    { icon: IconBlur, label: "Blur" },
    { icon: IconDownsize, label: "Scale" },
  ],
  none: [{ icon: IconCircleX, label: "No Animation" }],
  opacity: [{ icon: IconColors, label: "Opacity" }],
};

const HIDDEN: Record<Exclude<Swap, "none">, TargetAndTransition> = {
  all: { filter: "blur(4px)", opacity: 0, scale: 0.25 },
  opacity: { opacity: 0 },
};

const VISIBLE: Record<Exclude<Swap, "none">, TargetAndTransition> = {
  all: { filter: "blur(0px)", opacity: 1, scale: 1 },
  opacity: { opacity: 1 },
};

function AnimationLegend({ swap }: { swap: Swap }) {
  return (
    <div className="absolute -bottom-10 left-1/2 flex -translate-x-1/2 items-center justify-center">
      {LEGENDS[swap].map(({ icon: Icon, label }, index) => (
        <Tooltip
          key={label}
          label={label}
          render={
            <div
              className={cn(
                "flex size-7 items-center justify-center rounded-full bg-gray-300 transition-colors duration-200 ease-out hover-hover:hover:bg-gray-400",
                index > 0 && "-ml-1 ring-2 ring-preview-bg"
              )}
            >
              <Icon className="size-5 text-gray-900" />
            </div>
          }
        />
      ))}
    </div>
  );
}

function CopyIcon({ copied }: { copied: boolean }) {
  return copied ? (
    <IconCircleCheck className="size-7 text-gray-1100 will-change-transform" />
  ) : (
    <IconSquareBehindSquare2 className="size-7 text-gray-1100 will-change-transform" />
  );
}

function CopyButton({ swap }: { swap: Swap }) {
  const { isCopied, markCopied } = useCopyState(DEMO_CONFIRMATION_MS);

  return (
    <div className="relative flex flex-col items-center justify-center">
      <m.button
        aria-label={isCopied ? "Copied" : "Copy"}
        className={BUTTON_CLASS}
        initial={false}
        onClick={markCopied}
        transition={SPRING_FAST}
        type="button"
        whileTap={{ scale: 0.95 }}
      >
        {swap === "none" ? (
          <CopyIcon copied={isCopied} />
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
            <m.div
              animate={VISIBLE[swap]}
              className="will-change-transform"
              exit={HIDDEN[swap]}
              initial={HIDDEN[swap]}
              key={isCopied ? "check" : "copy"}
              transition={SPRING_FAST}
            >
              <CopyIcon copied={isCopied} />
            </m.div>
          </AnimatePresence>
        )}
      </m.button>
      <AnimationLegend swap={swap} />
    </div>
  );
}

export default function AnimationIconCrossfadeDemo() {
  return (
    <DemoWrapper margin="">
      <TooltipProvider>
        <DemoSplit stackBelow="sm">
          {SWAPS.map((swap) => (
            <DemoPanel innerClassName="h-44 pb-10" key={swap}>
              <CopyButton swap={swap} />
            </DemoPanel>
          ))}
        </DemoSplit>
      </TooltipProvider>
    </DemoWrapper>
  );
}
